import BaseComponent from './BaseComponent';
import { gsap } from 'gsap';

export default class Nav extends BaseComponent {
	constructor(elm, pageHeader) {
		super();

		// Elements
		this.pageHeader = pageHeader;
		this.elm = elm;
		this.submenus = this.elm.querySelectorAll('[data-nav-submenu]');
		this.submenuLinks = this.elm.querySelectorAll('a');

		this.buttons = this.elm.querySelectorAll('[data-nav-button]');
		this.buttons.forEach((button) => {
			button.addEventListener('click', () => {
				const submenu = button.closest('[data-nav-main-item]').querySelector('[data-nav-submenu]');
				this.expandSubmenu(submenu);
				this.submenus.forEach((otherSubmenu) => {
					if (otherSubmenu !== submenu) {
						this.collapseSubmenu(otherSubmenu);
					}
				});
				if (!this.pageHeader.isMobile.matches) {
					const submenuLinks = submenu.querySelectorAll('a');
					if (submenuLinks.length > 0) {
						submenuLinks[0].click();
					}
				}
			});

			button.addEventListener('mouseenter', () => {
				if (this.pageHeader.isMobile.matches) {
					return;
				}

				const submenu = button.closest('[data-nav-main-item]').querySelector('[data-nav-submenu]');
				this.expandSubmenu(submenu);
			});

			button.addEventListener('focus', () => {
				if (this.pageHeader.isMobile.matches) {
					return;
				}

				this.pageHeader.maximize();

				const submenu = button.closest('[data-nav-main-item]').querySelector('[data-nav-submenu]');
				this.expandSubmenu(submenu);

				this.submenus.forEach((otherSubmenu) => {
					if (otherSubmenu !== submenu) {
						this.collapseSubmenu(otherSubmenu);
					}
				});
			});
		});

		this.mainItems = this.elm.querySelectorAll('[data-nav-main-item]');
		this.mainItems.forEach((item) => {
			item.addEventListener('mouseenter', () => {
				if (this.pageHeader.isMobile.matches) {
					return;
				}
				this.pageHeader.showBlur();
			});

			item.addEventListener('mouseleave', () => {
				if (this.pageHeader.isMobile.matches) {
					return;
				}
				const submenu = item.querySelector('[data-nav-submenu]');
				this.collapseSubmenu(submenu);

				this.pageHeader.hideBlur();
			});
		});

		this.setInitialState();
	}

	setInitialState() {
		this.checkForCurrentMenuItem();
		if (this.pageHeader.isMobile.matches) {
			// Mobile
			this.submenus.forEach((submenu) => {
				gsap.set(submenu, {
					height: 0,
					autoAlpha: 1,
				});

				const items = submenu.querySelectorAll('[data-nav-subitem]');
				gsap.set(items, {
					autoAlpha: 0,
				});
			});

			const initialMenu = this.checkForInitialSubenu();
			let submenu = this.submenus[0];
			if (initialMenu) {
				submenu = initialMenu;
			}
			this.expandSubmenu(submenu, true);
			this.submenus.forEach((otherSubmenu) => {
				if (otherSubmenu !== submenu) {
					this.collapseSubmenu(otherSubmenu, true);
				}
			});
		} else {
			// Desktop
			this.submenus.forEach((submenu) => {
				this.collapseSubmenu(submenu, true);
			});
		}
	}

	checkForInitialSubenu() {
		let fallbackLink = null;

		for (const link of this.submenuLinks) {
			if (link.href === window.location.href) {
				link.classList.add('current-menu-item');
				return link.closest('[data-nav-submenu]');
			}

			const linkURL = new URL(link.href);
			if (linkURL.pathname === window.location.pathname && !fallbackLink) {
				fallbackLink = link;
			}
		}

		if (fallbackLink) {
			fallbackLink.classList.add('current-menu-item');
			return fallbackLink.closest('[data-nav-submenu]');
		}
	}

	checkForCurrentMenuItem() {
		let fallbackLink = null;

		this.submenuLinks.forEach((link) => {
			link.classList.remove('current-menu-item');
		});

		for (const link of this.submenuLinks) {
			if (link.href === window.location.href) {
				link.classList.add('current-menu-item');
				return;
			}

			const linkURL = new URL(link.href);
			if (linkURL.pathname === window.location.pathname && !fallbackLink) {
				fallbackLink = link;
			}
		}

		if (fallbackLink) {
			fallbackLink.classList.add('current-menu-item');
		}
	}

	expandSubmenu(submenu, instant = false) {
		const submenuInner = submenu.querySelector('[data-nav-submenu-inner]');
		const contentHeight = submenuInner.clientHeight;

		gsap.to(submenu, {
			height: contentHeight,

			duration: instant ? 0 : this.pageHeader.isMobile.matches ? 0.3 : 0,
			delay: instant ? 0 : this.pageHeader.isMobile.matches ? 0.1 : 0,
			ease: 'sine.inOut',
			overwrite: true,
		});

		const items = submenu.querySelectorAll('[data-nav-subitem]');
		if (!items) return;
		items.forEach((item) => {
			const link = item.querySelector('a');
			if (!link) return;
			link.removeAttribute('tabindex');
		});
		gsap.to(items, {
			autoAlpha: 1,

			duration: instant ? 0 : this.pageHeader.isMobile.matches ? 0.4 : 0.2,
			delay: instant ? 0 : this.pageHeader.isMobile.matches ? 0.4 : 0,
			ease: 'none',
			overwrite: true,
			onComplete: () => {
				if (this.pageHeader.isMobile.matches) {
					this.pageHeader.menu.trapFocus(this.pageHeader.menu.elm);
				} else {
					this.pageHeader.menu.trapFocus(this.pageHeader.menu.nav);
				}
			},
		});
	}

	collapseSubmenu(submenu, instant = false) {
		const items = submenu.querySelectorAll('[data-nav-subitem]');
		if (!items) return;
		items.forEach((item) => {
			const link = item.querySelector('a');
			if (!link) return;
			link.setAttribute('tabindex', '-1');
		});

		gsap.to(items, {
			autoAlpha: 0,

			duration: instant ? 0 : this.pageHeader.isMobile.matches ? 0.2 : 0.2,
			delay: instant ? 0 : 0,
			ease: 'none',
			overwrite: true,
		});

		gsap.to(submenu, {
			height: 0,

			duration: instant ? 0 : this.pageHeader.isMobile.matches ? 0.3 : 0,
			delay: instant ? 0 : this.pageHeader.isMobile.matches ? 0.1 : 0,
			ease: 'sine.inOut',
			overwrite: true,
		});
	}

	closeMenus() {
		this.submenus.forEach((submenu) => {
			this.collapseSubmenu(submenu);
		});
	}
}
